import React, { Component } from 'react'
import PropTypes from 'prop-types'
import pickby from 'lodash.pickby'
import identity from 'lodash.identity'
import { CookiesProvider } from 'react-cookie'
import Cookies from 'universal-cookie'
import { parse } from 'query-string'
import ClientLibrary from 'utils/client'
import ClientInfoService from 'utils/info-service'
import { createLogger } from '@cred/fe-logger'
import config from 'config'
import gtm from '@cred/shared/gtm'
import { populatedGtmDataLayer } from '@cred/react-analytics/utils/data'
import Snowplow from '@cred/react-analytics/Snowplow'
import { ThemeContext } from '@cred/react-theme'
import ApplicationErrorBoundary from 'components/error-boundary'
import { extractHostname } from 'utils/urls'
import { v4 as uuid } from 'uuid'
import WidgetContext from 'components/widget-context'
import InitialValuesContext from 'components/initial-values-context'
import styles from './WidgetApp.module.scss'
import 'purecss/build/pure.css' /* Importing Pure here otherwise the import order gets incorrect. */
import 'purecss/build/grids-responsive.css'
import 'iframe-resizer/js/iframeResizer.contentWindow.min.js'
import '@cred/global-assets/styles/common.scss'
import '@cred/global-assets/styles/cred-icon.scss'

/**
  All widgets must have the same url structure
  /partners-widgets/:marketplace/:widgetType/

  (React Router is not being used here on purpose,
  now each widget has it own entry point)
*/
function getRouteParams(pathname) {
  const parts = pathname.split('/').filter(Boolean)

  if (parts.length < 2) {
    return {}
  }

  return {
    marketplace: parts[parts.length - 2],
    widgetType: parts[parts.length - 1],
  }
}

function getQueryParams(search) {
  return parse(search)
}

function getMarketplaceParam(marketplace, marketplaces) {
  const isMultiVertical = marketplace === 'multi-vertical'

  return isMultiVertical ? marketplaces.split(',') : [marketplace]
}

function parseMultiVerticalOptions(options) {
  try {
    return JSON.parse(options)
  } catch (_e) {
    return {}
  }
}

class App extends Component {
  static propTypes = {
    children: PropTypes.node.isRequired,
  }

  constructor(props) {
    super(props)

    this.params = getRouteParams(window.location.pathname)
    this.query = getQueryParams(window.location.search)

    const {
        meta_contentId: contentId,
        meta_articleTitle: articleTitle,
        meta_articleTags: articleTags,
        meta_segmentId: segmentId,
        meta_campaignCode: campaignCode,
        meta_contentDistributor: contentDistributor,
        meta_contentCategory: contentCategory,
        meta_campaignCodeExt: campaignCodeExt,
        theme,
        variation,
        pageUrl,
        marketplaces,
        defaultFilters,
        zipCode,
        activeTab,
        loanType,
        terms,
        sorting,
        omitUtmParams,
        multiVerticalOptions,
        isJumbo,
        ...options
      } = this.query,
      { marketplace, widgetType } = this.params

    // Initialize client library communication channel
    this.clientLibrary = new ClientLibrary()

    // Initialize client information service
    this.clientInfoService = new ClientInfoService(this.clientLibrary)

    // Initialize the Cookies
    this.cookies = new Cookies()

    // Push Segemnt needed variables to GTM Data Layer
    populatedGtmDataLayer({
      contentId,
      articleTitle,
      articleTags,
      campaignCode,
      campaignCodeExt,
      contentDistributor,
      contentCategory,
      segmentId,
      pageUrl,
    })

    // Initialize GTM service
    gtm.initialize(config.gtmContainerId, this.cookies)

    // Initialize the logger component
    createLogger({}, { credibleRoot: config.credibleRoot })

    this.state = {
      // Options provided by partner.
      // These fallback values we need for:
      // - test widget in QA environment when host page is missing;
      // - local development.
      // Once widget is embedded into partner site these options will be replaced
      // by partners specific.
      options: {
        ui: {
          theme,
        },
        product: {
          type: widgetType,
          marketplace: getMarketplaceParam(marketplace, marketplaces),
          variation,
        },
        meta: {
          contentId,
          articleTitle,
          articleTags,
          segmentId,
          campaignCode,
          contentDistributor,
          contentCategory,
          campaignCodeExt,
        },
        multiVertical: parseMultiVerticalOptions(multiVerticalOptions),
        omitUtmParams,
      },
      isJumbo,
      initialValues: {
        activeTab,
        zipCode,
        loanType,
        terms,
        sorting,
        options,
      },
      // Global Snowplow context provided by Credible SDK.
      parentPage: {
        partnerReferrer: pageUrl || document.referrer,
        partnerDomain: extractHostname(document.referrer),
        partnerURL: pageUrl || document.referrer,
      },
      credclid: this.query.credclid || uuid(),
      renderId: uuid(),
      source: this.query.source,
    }
  }

  async componentDidMount() {
    // Analytics initialization
    Snowplow.initialize()

    await this.setOptionsSuppliedByPartner()

    await this.setSnowplowContext()

    this.scrollToTop()

    this.clientLibrary.sendMessage('onInit')
  }

  componentWillUnmount() {
    this.clientLibrary.sendMessage('onDestroy')
  }

  async setOptionsSuppliedByPartner() {
    const options = await this.clientInfoService.optionsDeferred

    this.setState({
      options,
    })
  }

  async setSnowplowContext() {
    const parentPage = await this.clientInfoService.snowplowContextDeferred

    this.setState({
      parentPage,
    })
  }

  scrollToTop() {
    // Request client library perform scroll
    this.clientLibrary.sendMessage('scrollRequest', 'top')
  }

  render() {
    const { options, parentPage, credclid, renderId, source, initialValues, isJumbo } = this.state,
      theme = options.ui.theme === 'fox' ? 'fox1' : options.ui.theme,
      { children } = this.props

    return (
      <div className={styles.container}>
        <ApplicationErrorBoundary clientInfoService={this.clientInfoService} clientLibrary={this.clientLibrary}>
          <CookiesProvider cookies={this.cookies}>
            <WidgetContext.Provider value={{ options, parentPage, credclid, renderId, source }}>
              <InitialValuesContext.Provider value={{ isJumbo, initialValues: pickby(initialValues, identity) }}>
                <ThemeContext.Provider value={theme}>
                  {children}
                </ThemeContext.Provider>
              </InitialValuesContext.Provider>
            </WidgetContext.Provider>
          </CookiesProvider>
        </ApplicationErrorBoundary>
      </div>
    )
  }
}

export {
  getRouteParams,
  getQueryParams,
  parseMultiVerticalOptions,
}

export default App
